import type { AuditEventDto } from '@fruuit/shared'
import { Callout, Drawer, SkeletonText } from '@fruuit/ui'
import type { ReactNode } from 'react'
import { errorMessage } from '../../api/client'
import { formatDateTime } from '../records/format'
import { useAuditEvent } from './api'

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-semibold tracking-[0.06em] text-sage uppercase">{label}</dt>
      <dd className="mt-1 text-sm break-words text-ink">{children}</dd>
    </div>
  )
}

/** S15 event detail: re-read by (chain, seq), with the full before/after details and hashes. */
export function AuditEventDrawer({
  event,
  chainLabel,
  onClose,
}: {
  event: Pick<AuditEventDto, 'chain' | 'seq'> | null
  chainLabel?: (chain: string) => string
  onClose: () => void
}) {
  const query = useAuditEvent(event?.chain ?? null, event?.seq ?? null)
  const data = query.data
  return (
    <Drawer
      open={event !== null}
      onClose={onClose}
      title={event ? `Audit event #${event.seq}` : 'Audit event'}
      className="w-[min(40rem,100vw)]!"
    >
      <div className="px-5 py-5">
        {query.isError ? (
          <Callout tone="danger" role="alert">
            {errorMessage(query.error)}
          </Callout>
        ) : !data ? (
          <SkeletonText lines={8} />
        ) : (
          <div className="flex flex-col gap-5">
            <dl className="grid gap-4 sm:grid-cols-2">
              <Row label="When">{formatDateTime(data.ts)}</Row>
              <Row label="Actor">
                {data.actor_name ?? data.actor_id ?? 'System'}
                {data.impersonated_by ? (
                  <span className="block text-xs text-watch">
                    Impersonated by {data.impersonated_by}
                  </span>
                ) : null}
              </Row>
              <Row label="Action">
                <code className="text-[13px]">{data.action}</code>
              </Row>
              <Row label="Record">
                {data.kind}
                {data.record_id ? (
                  <span className="block font-mono text-xs text-sage">{data.record_id}</span>
                ) : null}
              </Row>
              <Row label="Chain">
                {chainLabel ? chainLabel(data.chain) : data.chain} · #{data.seq}
              </Row>
              <Row label="Request id">
                <span className="font-mono text-xs">{data.request_id ?? '—'}</span>
              </Row>
            </dl>
            <section>
              <h3 className="mb-2 text-xs font-semibold tracking-[0.06em] text-sage uppercase">
                Details (before / after)
              </h3>
              <pre className="max-h-96 overflow-auto rounded-md border border-line bg-cream p-3 font-mono text-xs leading-relaxed text-ink">
                {JSON.stringify(data.details, null, 2)}
              </pre>
            </section>
            <dl className="flex flex-col gap-3">
              <Row label="Hash">
                <span className="font-mono text-xs break-all">{data.hash}</span>
              </Row>
              <Row label="Previous hash">
                <span className="font-mono text-xs break-all">{data.prev_hash}</span>
              </Row>
            </dl>
            <Callout>
              Database triggers block audit updates and deletion, and each hash covers the event and
              the hash before it. The chain heads are anchored nightly outside the database.
            </Callout>
          </div>
        )}
      </div>
    </Drawer>
  )
}
